import React, { useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { Duration } from 'luxon';
import classNames from 'classnames';
import { Checkbox, DatePicker, Icon, Input } from '@fikasio/react-ui-components';
import TrashIcon from 'components/UI/TrashIcon';
import ProjectStatusDropdown from './ProjectStatusDropdown';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import { statusChangePatch } from 'components/tasks/TasksViewer/statusChange';
import CollapseToggle from 'components/UI/CollapseToggle';
import DropdownToggle from 'components/UI/DropdownToggle';
import { formatPickerDate, isDueDatePast, pickedDateTimeInUserZone, toPickerDate } from 'utils/date';
import { useAddProjectMutation, usePatchProjectMutation, useDeleteProjectMutation } from 'services/projects/api';
import { Project } from 'services/projects/types';
import { RangeSelectEvent } from 'utils/rangeSelection';
import { MAX_PROJECT_DEPTH, projectStatus } from './utils';
import { round } from 'utils/maths';
import styles from './ProjectsList.module.css';

type ProjectWithStats = Project & {
  completionRatio?: number | null;
  incompleteTime?: number | null;
};

export interface ProjectRowProps {
  canCollapse?: boolean;
  depth?: number;
  descendantDepth?: number;
  isCollapsed?: boolean;
  isSelected?: boolean;
  onAddProject: (project: Partial<Project>) => Promise<void> | void;
  onClick: (projectId: string) => Promise<void>;
  onEditingNameChange?: (editing: boolean) => void;
  onSelect: (project: Project, event?: RangeSelectEvent) => void;
  onToggleCollapsed?: () => void;
  parent?: Project;
  previousSibling?: Project;
  project: ProjectWithStats;
}

const caretAtStart = (target: EventTarget | null) => {
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.selectionStart === 0 && target.selectionEnd === 0;
  }
  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  if (!selection || !selection.isCollapsed || selection.rangeCount === 0) {
    return false;
  }
  return selection.getRangeAt(0).startOffset === 0;
};

const ProjectRow = ({
  canCollapse = false,
  depth = 0,
  descendantDepth = 0,
  isCollapsed = false,
  isSelected,
  onAddProject,
  onClick,
  onEditingNameChange,
  onSelect,
  onToggleCollapsed,
  parent,
  previousSibling,
  project: pProject,
}: ProjectRowProps) => {
  const { t } = useTranslation();

  const [project, setProject] = useState(pProject);
  const [name, setName] = useState((project && project.name) || '');
  const [hasFocus, setHasFocus] = useState(false);
  const [isDueAtDatepickerOpen, setIsDueAtDatepickerOpen] = useState(false);

  const [createProject] = useAddProjectMutation();
  const [patchProject] = usePatchProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();

  useEffect(() => {
    setProject(pProject);
  }, [
    pProject.cancelReason,
    pProject.dueAt,
    pProject.isArchived,
    pProject.assignee,
    pProject.name,
    pProject.parentProject,
    pProject.status,
  ]);

  useEffect(() => {
    if (!hasFocus) {
      setName(project.name);
    }
  }, [hasFocus, project]);

  const handleBlur = () => {
    setHasFocus(false);
    onEditingNameChange?.(false);
  };

  const handleFocus = () => {
    setHasFocus(true);
    onEditingNameChange?.(true);
  };

  const handleKeyDownName = e => {
    if (e.key === 'Tab' && project.id && caretAtStart(e.target)) {
      if (e.shiftKey && project.parentProject) {
        e.preventDefault();
        patchProject({
          id: project.id,
          parentProject: parent?.parentProject ?? null,
        });
      } else if (
        !e.shiftKey
        && previousSibling?.id
        && depth + 1 + descendantDepth <= MAX_PROJECT_DEPTH
      ) {
        e.preventDefault();
        patchProject({
          id: project.id,
          parentProject: previousSibling.id,
        });
      }
      return;
    }
    if (e.key === 'Enter') {
      onAddProject({
        name: '',
        ...(project.parentProject ? { parentProject: project.parentProject } : {}),
      });
      e.preventDefault();
    }
  };

  const handleKeyUpName = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, updatedProject) => {
    const value = e.currentTarget.value;
    if (updatedProject) {
      if (e.key === 'Backspace' && value === '') {
        deleteProject(updatedProject.id);
      }
    } else if (value !== '') {
      createProject({
        name: value,
      });
    }
  };

  const status = projectStatus(project);
  const isClosed = status === 'Completed' || status === 'Cancelled';
  const { completionRatio, incompleteTime } = pProject;

  const handleAssigneeChange = (assignee: string | null) => {
    setProject({
      ...project,
      assignee,
    });
    if (project.id) {
      patchProject({
        assignee,
        id: project.id,
      });
    }
  };

  const handleStatusChange = (
    nextStatus: NonNullable<Project['status']>,
    extras?: { cancelReason?: string | null },
  ) => {
    setProject({
      ...project,
      cancelReason: nextStatus === 'Cancelled' ? extras?.cancelReason ?? null : null,
      isArchived: false,
      status: nextStatus,
    });
    if (project.id) {
      patchProject({
        id: project.id,
        ...statusChangePatch(nextStatus, {
          cancelReason: extras?.cancelReason,
          wasArchived: project.isArchived,
        }),
      });
    }
  };

  const hasdueAtPassed = project && isDueDatePast(project.dueAt);

  return (
    <tr
      className={classNames({
        [styles.projectRow]: true,
        done: isClosed && !project.isArchived,
        selected: isSelected,
      })}
    >
      <td
        onClick={event => {
          event.stopPropagation();
          onSelect(project, event);
        }}
        onMouseDown={event => {
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Checkbox isChecked={isSelected} />
      </td>
      <td
        className={styles.projectRowStatus}
        width={35}
      >
        <ProjectStatusDropdown
          onArchived={() => setProject(current => ({
            ...current,
            isArchived: true,
          }))}
          onChange={handleStatusChange}
          project={project}
          status={status}
        />
      </td>
      <td
        onClick={event => {
          if (event.shiftKey) {
            event.preventDefault();
            onSelect(project, event);
            return;
          }
          onClick(project.id);
        }}
        onMouseDown={event => {
          if (event.shiftKey) {
            event.preventDefault();
          }
        }}
        style={{
          cursor: 'pointer',
          paddingLeft: 8 + (depth * 24),
        }}
      >
        <div className={styles.projectRowNameContent}>
          {
            canCollapse && (
              <CollapseToggle
                isCollapsed={isCollapsed}
                onToggle={() => onToggleCollapsed?.()}
              />
            )
          }
          <Input
            className={classNames({
              [styles.projectRowNameEditable]: true,
              [project.id]: true,
            })}
            defaultValue={name}
            onBlur={() => handleBlur()}
            onFocus={() => handleFocus()}
            onKeyDown={e => handleKeyDownName(e)}
            onKeyUp={e => handleKeyUpName(e, project)}
            onSave={async value => {
              if (project.id) {
                patchProject({
                  id: project.id,
                  name: value,
                });
              } else {
                createProject({
                  ...project,
                  name: value,
                })
                  .then(({ data: resultProject }) => {
                    if (resultProject) {
                      setProject(resultProject);
                    }
                  });
              }
            }}
            style={{
              flex: '1 1 auto',
              height: 25,
              minWidth: 0,
              paddingLeft: 5,
              paddingRight: 50,
              paddingTop: 0,
              width: 'auto',
            }}
          />
        </div>
      </td>
      <td width={150}>
        {
          incompleteTime != null
            ? Duration.fromMillis(incompleteTime * 60 * 1000).toFormat('h:mm')
            : '-'
        }
      </td>
      <td width={150}>
        {
          completionRatio != null
            ? `${round(completionRatio * 100, 1)}%`
            : '-'
        }
      </td>
      <td
        className={classNames('projectRow_dueAt', styles.projectRowDate)}
        onClick={() => setIsDueAtDatepickerOpen(true)}
        style={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          ...(!isClosed && hasdueAtPassed && { color: '#ff0000' }),
          minWidth: 150,
        }}
      >
        <DatePicker
          defaultValue={toPickerDate(project.dueAt)}
          displayFunction={formatPickerDate}
          isOpen={isDueAtDatepickerOpen}
          name="dueAt"
          onChange={value => {
            const timestamp = pickedDateTimeInUserZone(value)
              .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
            patchProject({
              id: project.id,
              dueAt: timestamp.toJSDate(),
            });
          }}
          onClose={() => setIsDueAtDatepickerOpen(false)}
          onRemoveValue={e => {
            e.stopPropagation();
            patchProject({
              id: project.id,
              dueAt: null,
            });
          }}
          shouldCloseOnSelect
          showRemoveValue
          showTimeSelect={false}
        />
      </td>
      <td
        className={styles.projectRowAssignee}
        width={35}
      >
        <AssigneeButton
          assigneeId={project.assignee || undefined}
          onChange={handleAssigneeChange}
        />
      </td>
      <td
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Dropdown
          style={{
            position: 'static',
          }}
        >
          <Dropdown.Toggle as={DropdownToggle} />
          <Dropdown.Menu>
            {
              project.id && depth < MAX_PROJECT_DEPTH && (
                <Dropdown.Item
                  onClick={() => onAddProject({
                    name: '',
                    parentProject: project.id,
                  })}
                >
                  <Icon
                    name="plus"
                    style={{
                      marginRight: 10,
                      width: 25,
                    }}
                  />
                  {t('addSubproject')}
                </Dropdown.Item>
              )
            }
            <Dropdown.Item
              onClick={() => createProject(project)}
            >
              <Icon
                name="copy"
                style={{
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('duplicate')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => deleteProject(project.id)}
            >
              <TrashIcon
                style={{
                  color: 'red',
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('delete')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );
};

export default ProjectRow;
